// @ts-strict-ignore
import moment from 'moment';
import type {Moment} from 'moment';

import {ReportName} from 'common/report/constants';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {getLocation} from 'common/util/url';
import {Translatable} from 'common/i18n';
import {CurrentProject} from 'common/report/model-resources/projects';
import {ShowClauseComputedAt} from 'common/report/types';

import {isSessionRecordingEnabled} from '../feature-sampling';

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;

export const LINKED_COPY_LABEL = ({t}) => t(`boards.actions.linked-copy`);
export const DUPLICATE_LABEL = ({t}) => t(`boards.actions.duplicate`);

export const DUPLICATE_REPORT_PLACEHOLDER = ({t}) => t(`boards.cards.actions.duplicate-placeholder`);
export const MOVE_REPORT_PLACEHOLDER = ({t}) => t(`boards.cards.actions.move-placeholder`);

export const DUPLICATE_REPORT_COPY = ({t}) => t(`boards.cards.actions.add-to-new-board`);
export const MOVE_REPORT_COPY = ({t}) => t(`reports.actions.move-to-board`);

export const REPORT_ICONS = {
  [ReportName.Insights]: `brand-insights`,
  [ReportName.Funnels]: `brand-funnels`,
  [ReportName.Flows]: `brand-flows`,
  [ReportName.Retention]: `brand-retention`,
  [ReportName.Dashboards]: `brand-dashboard`,
};

const LinkedReportIcons = {
  [ReportName.Insights]: `brand-insights-linked`,
  [ReportName.Funnels]: `brand-funnels-linked`,
  [ReportName.Flows]: `brand-flows-linked`,
  [ReportName.Retention]: `brand-retention-linked`,
};

export function getReportIcon(type: BookmarkType, isLinked = false) {
  return isLinked ? LinkedReportIcons[type] : REPORT_ICONS[type];
}

export function getCardInDashboardHref({
  dashboardId,
  bookmarkId,
  cardId,
  projectId,
  workspaceId,
  rowId,
}: {
  dashboardId: number;
  bookmarkId?: number;
  cardId?: number | string;
  projectId: number;
  workspaceId: number;
  rowId?: string;
}) {
  const base = getReportUrl(`boards`, {
    projectId,
    workspaceId,
  });
  const hashParams = [];
  if (dashboardId) {
    hashParams.push(`id=${dashboardId}`);
  }
  if (bookmarkId) {
    hashParams.push(`focused-bookmark-id=${bookmarkId}`);
  } else if (cardId) {
    hashParams.push(`card-id=${cardId}`);
  } else if (rowId) {
    hashParams.push(`row-id=${rowId}`);
  }

  return hashParams.length ? `${base}#${hashParams.join(`&`)}` : base;
}

export function getDashboardAllowedReports(currentProject: CurrentProject | null): ReportName[] {
  return [
    ReportName.Insights,
    ReportName.Funnels,
    ReportName.Flows,
    ReportName.Retention,
    isSessionRecordingEnabled(currentProject) ? ReportName.HeatMaps : false,
    isSessionRecordingEnabled(currentProject) ? ReportName.HeatMapCollections : false,
    ReportName.Playlists,
  ].filter<ReportName>((r): r is ReportName => !!r);
}

/**
 * @todo - [SRFE-8808]: Remove exclusion of heat maps and playlists.
 * These are supposed to work, but their usage of UUID as a pkey breaks the linking functionality.
 */
export const BROWSE_ADD_MODE_EXCLUDED_REPORTS = [
  ReportName.HeatMaps,
  ReportName.HeatMapCollections,
  ReportName.Playlists,
];

export function getFullReportUrl(appName: string, hash: string) {
  const {protocol, host} = getLocation();
  const path = getReportUrl(`${appName}#${hash}`, {projectId: getProjectId(), workspaceId: getWorkspaceId()});
  return `${protocol}//${host}${path}`;
}

export function getLastRefreshedTimeLabel(lastUpdatedTime: Moment) {
  const now = moment();
  let timeAgoLabel: Translatable;
  if (now.diff(lastUpdatedTime, `minutes`) < 1) {
    timeAgoLabel = ({t}) => t(`reports.actions.data-age-recent`);
  } else if (now.diff(lastUpdatedTime, `minutes`) < 60) {
    timeAgoLabel = ({t}) => t(`reports.actions.data-age-hour`, {minutes: now.diff(lastUpdatedTime, `minutes`)});
  } else {
    timeAgoLabel = ({t, timeFromNow}) => t(`reports.actions.data-age-old`, {time: timeFromNow(lastUpdatedTime)});
  }

  return timeAgoLabel;
}

type ResultsWithShowClauses = {
  computed_at?: string;
  meta?: {report_sections?: {show?: ShowClauseComputedAt[]}};
};

/**
 * Get overall freshness for a card from its metrics individual computed_at values
 * Useful for warehouse metrics which can be staler than their native counterparts
 */
export function getCardComputedAt(results: ResultsWithShowClauses): string | undefined {
  const timestamps = (results.meta?.report_sections?.show ?? [])
    .map((clause) => clause.computed_at)
    .filter((ts): ts is string => Boolean(ts));
  if (timestamps.length) {
    // ISO-8601 UTC timestamps sort chronologically as strings, so min = oldest.
    return timestamps.reduce((oldest, ts) => (ts < oldest ? ts : oldest));
  }
  return results.computed_at;
}

export function getRouteToCurrentDashboard({
  dashboard,
  includeCurrentDashboard = false,
}: {
  dashboard?: DashboardOrigin;
  includeCurrentDashboard?: boolean;
}): DashboardOrigin[] | null {
  const currentDashboardInfo = includeCurrentDashboard ? [{id: dashboard?.id, title: dashboard?.title}] : [];

  if (!dashboard?.ancestors) {
    return currentDashboardInfo;
  }

  return [...dashboard.ancestors, ...currentDashboardInfo];
}

/**
 * Determine whether to auto-refresh. TTLs should be kept roughly in sync with Query API.
 *
 * @param computedAt the date the results were computed at.
 * @param fromDate the fromDate of the report.
 * @param toDate the toDate of the report.
 * @param unit the time granularity unit of the report.
 * @returns true if we should auto-refresh this report, false otherwise.
 */
export function isResultsOutsideRefreshTtl(
  computedAt: string,
  fromDate?: string,
  toDate?: string,
  unit?: string,
): boolean {
  if (!computedAt) {
    return false;
  }

  let numHours = null;
  let numDays = null;
  if (fromDate && toDate) {
    numHours = moment(toDate).diff(moment(fromDate), `hours`);
    numDays = moment(toDate).diff(moment(fromDate), `days`);
  }

  // The following should be kept *loosely* in sync with the values in Query API at:
  // api/version_2_0/cache/arb_query_cache.py#_dashboard_refresh_interval
  // It improves the user experience and reduces the amount of unnecessary polling
  // when the values sync up nicely. In Query API if the cached result is over these
  // TTLs then it will issue a background refresh request.
  // However we actually don't want to background refresh automatically TOO much as
  // this will be disruptive to the UX, so these values are larger than what's in
  // Query API.
  let refreshTtl = 2 * SECONDS_PER_HOUR;
  if (numHours !== null && numDays !== null) {
    if (numDays <= 31) {
      refreshTtl = 30 * SECONDS_PER_MINUTE;
    } else if (numDays <= 62) {
      refreshTtl = 2 * SECONDS_PER_HOUR;
    } else if (numDays <= 186) {
      refreshTtl = 6 * SECONDS_PER_HOUR;
    } else if (numDays <= 366) {
      refreshTtl = 6 * SECONDS_PER_HOUR;
    } else {
      refreshTtl = 12 * SECONDS_PER_HOUR;
    }
  }

  if (unit === `minute`) {
    refreshTtl = Math.floor(refreshTtl / 4);
  } else if (unit === `hour`) {
    refreshTtl = Math.floor(refreshTtl / 2);
  }

  const ageOfCachedResult: number = moment().diff(moment(computedAt), `seconds`);
  return ageOfCachedResult >= refreshTtl;
}
